import React, { useEffect, useState } from 'react';
import { parse } from 'query-string';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery, useMutation } from '@apollo/client';

import {
  PROCESS_CARD_PRR_REFUND,
  PROCESS_VOUCHER_PRR_REFUND,
  PROCESS_BUSINESS_ACCOUNT_PRR_REFUND,
  PROCESS_LOYALTY_PRR_REFUND
} from 'store/mutations';
import AgentPortalError from 'errors/AgentPortalError';
import { useManagedMutation, useManagedQuery } from 'utils/managedHooks';
import { codes as paymentCodes, providers as paymentProviders } from 'constants/payment';
import { historyShape, userShape, bookingShape, locationShape } from 'constants/propTypes';
import { withUser } from 'components/queries/GetUserQuery';
import { withBooking } from 'components/queries/GetBookingQuery';
import { hasHotel } from 'utils/booking';
import { getBookingJourneysURL } from 'utils/urls';
import { GET_BOOKING_ITEMS } from 'store/queries';
import config from 'utils/config';
import { getSegmentsForItemRefs } from 'pages/BookingDetails/Journeys/AfterSalesCtas/AfterSalesCtas.utils';
import GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS from 'store/queries/GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS';
import getCostPriceForBookingItems from 'utils/getCostPriceForBookingItems';
import { FullPageSpinner } from 'admin-components/components';
import Modal from 'components/ui/Modal/Modal';
import { ERROR_MODAL_TYPE } from 'constants/prrRefund';

import PrrRefund from './PrrRefund';
import BOOKING_QUERY from './booking';
import { getCompensationErrorStatus, getRefundHandlers, prrRefundablePayments } from './PrrRefund.utils';

const Index = ({ user, booking, location: { search, state }, history: { push } }) => {
  const enableSplitDelayCompensation = config('ENABLE_SPLIT_DELAY_COMPENSATION', { canOverride: true }) === 'true';

  const { reference, allBookingItems, payments, customer, passengers, isPackage } = booking;

  const { itemRefs, membershipNumber } = parse(search);

  const [updateCompensationsInfo, setUpdateCompensationsInfo] = useState(null);

  const next = () => push(getBookingJourneysURL(reference, membershipNumber));

  const paymentLocation = user.paymentLocation;

  const selectedLegs = [...getSegmentsForItemRefs({ outbound: booking?.outbound ?? [], inbound: booking?.inbound ?? [] }, itemRefs)];
  const isThroughFare = selectedLegs.some(({ isThroughFare }) => isThroughFare);
  const ticketNumbers = selectedLegs
    .flatMap(({ products }) => products)
    .reduce((ticketNumbers, { ticketNumber, itemRef }) => {
      if (itemRefs.includes(itemRef)) {
        ticketNumbers.push(ticketNumber);
      }

      return ticketNumbers;
    }, []);

  const { data: getExtendedCompensationByTicketNumbersData, loading: getExtendedCompensationByTicketNumbersDataLoading } = useManagedQuery(
    GET_EXTENDED_COMPENSATION_BY_TICKET_NUMBERS,
    {
      variables: {
        ticketNumbers,
        bookingReference: reference
      },
      skip: !ticketNumbers?.length || isThroughFare || state?.shouldUseOldFlow
    }
  );

  const [getBookingItems, { data: bookingItemsData, loading: bookingItemsLoading }] = useLazyQuery(GET_BOOKING_ITEMS);

  const bookingItems = bookingItemsData?.getBookingItems;
  const compensationItems = getExtendedCompensationByTicketNumbersData?.getExtendedCompensationByTicketNumbers ?? [];

  const [processCardPrrRefund, { loading: isCardPrrRefund }] = useManagedMutation(PROCESS_CARD_PRR_REFUND, {
    onCompleted: ({ processCardPrrRefund = {} }) => {
      const { updatedCompensationRecords = [] } = processCardPrrRefund;

      if (compensationItems.length > 0 && updatedCompensationRecords.length !== compensationItems.length) {
        const compensationErrorStatus = getCompensationErrorStatus(ERROR_MODAL_TYPE.CARD, compensationItems, updatedCompensationRecords);

        if (compensationErrorStatus) {
          setUpdateCompensationsInfo(compensationErrorStatus);
          return;
        }
      }

      next();
    }
  });

  const [processVoucherPrrRefund, { loading: isVoucherPrrRefundLoading }] = useManagedMutation(PROCESS_VOUCHER_PRR_REFUND, {
    onCompleted: ({ processVoucherPrrRefund = {} }) => {
      const { updatedCompensationRecords = [] } = processVoucherPrrRefund;

      if (compensationItems.length > 0 && updatedCompensationRecords.length !== compensationItems.length) {
        const compensationErrorStatus = getCompensationErrorStatus(ERROR_MODAL_TYPE.VOUCHER, compensationItems, updatedCompensationRecords);

        if (compensationErrorStatus) {
          setUpdateCompensationsInfo(compensationErrorStatus);
          return;
        }
      }

      next();
    }
  });

  const [processBusinessAccountPrrRefund, { loading: isBusinessAccountPrrRefund }] = useManagedMutation(
    PROCESS_BUSINESS_ACCOUNT_PRR_REFUND,
    {
      onCompleted: ({ processBusinessAccountPrrRefund = {} }) => {
        const { updatedCompensationRecords = [] } = processBusinessAccountPrrRefund;

        if (compensationItems.length > 0 && updatedCompensationRecords.length !== compensationItems.length) {
          const compensationErrorStatus = getCompensationErrorStatus(
            ERROR_MODAL_TYPE.BUSINESS_ACCOUNT,
            compensationItems,
            updatedCompensationRecords
          );

          if (compensationErrorStatus) {
            setUpdateCompensationsInfo(compensationErrorStatus);
            return;
          }
        }

        next();
      }
    }
  );

  const [processLoyaltyPrrRefund, { loading: isProcessLoyaltyPrrRefund }] = useMutation(PROCESS_LOYALTY_PRR_REFUND);

  useEffect(() => {
    if (enableSplitDelayCompensation && isThroughFare) {
      getBookingItems({
        variables: {
          bookingReference: booking?.reference
        }
      });
    }
  }, [booking?.reference, enableSplitDelayCompensation, getBookingItems, isThroughFare]);

  const compensateLegs = selectedLegs.map((leg) => ({
    legName: `${leg.serviceType.code} ${leg.trainNumber}`,
    serviceType: leg.serviceType.code,
    isThroughFare: leg.isThroughFare,
    direction: leg.direction,
    products: leg.products
      .filter(({ itemRef }) => itemRefs.includes(itemRef))
      .map((product) => {
        const { price, uuid, associatedItemRefs, passenger, itemRef, currency, tariffDetails, loyaltyPoints, productFamily, ticketNumber } =
          product;

        let calculatedPrice = price;

        if (enableSplitDelayCompensation && isThroughFare && bookingItems?.length > 0) {
          calculatedPrice = getCostPriceForBookingItems(bookingItems, allBookingItems, price, uuid, associatedItemRefs);
        }

        return {
          itemRef,
          passengerType: passenger.type,
          bucketCode: tariffDetails.bucketCode,
          productFamily,
          currency,
          loyaltyPoints,
          price: calculatedPrice,
          ticketNumber
        };
      })
  }));

  const filteredProducts = compensateLegs.flatMap(({ products }) => products);

  const refundablePayments = prrRefundablePayments(payments);

  const { onRefundCard, onRefundApplePay, onRefundGooglePay } = getRefundHandlers({
    processCardPrrRefund
  });

  const handleSubmit = async (args) => {
    const { method: refundType, variables } = args;

    if (refundType === paymentCodes.EVOUCHER) {
      if (Array.isArray(variables)) {
        await Promise.allSettled(variables.map((args) => processVoucherPrrRefund({ variables: args })));
      } else {
        await processVoucherPrrRefund({ variables });
      }

      return;
    }

    if (refundType === paymentCodes.LOYALTY_POINTS) {
      const responses = await Promise.allSettled(
        variables.map((args) =>
          processLoyaltyPrrRefund({
            variables: args
          })
        )
      );

      const updatedCompensationRecords = responses.reduce((updatedRecords, { status, value }) => {
        if (status === 'fulfilled') {
          const compensations = value?.data?.processLoyaltyPrrRefund?.updatedCompensationRecords;

          if (compensations?.length > 0) {
            updatedRecords.push(...compensations);
          }
        }

        return updatedRecords;
      }, []);

      if (compensationItems.length > 0 && updatedCompensationRecords.length !== compensationItems.length) {
        const compensationErrorStatus = getCompensationErrorStatus(ERROR_MODAL_TYPE.LOYALTY, compensationItems, updatedCompensationRecords);

        if (compensationErrorStatus) {
          setUpdateCompensationsInfo(compensationErrorStatus);
          return;
        }

        next();
      } else {
        const nonRefunded = responses[0]?.reason;

        if (nonRefunded) {
          throw new AgentPortalError(nonRefunded);
        }

        next();
      }

      return;
    }

    if (refundType === paymentCodes.BUSINESS_ACCOUNT) {
      processBusinessAccountPrrRefund({
        variables
      });

      return;
    }

    const { currency, pspReference, bookingReference, refundAmount, additionalDetails, compensationRecordsToUpdate } = args.variables;
    const { method, provider } = refundablePayments.find((p) => p.reference === pspReference) || {};
    const refundMethod = {
      [paymentCodes.IDEAL]: onRefundCard,
      [paymentCodes.PAYPAL]: onRefundCard,
      [paymentCodes.APPLE_PAY]: onRefundApplePay,
      [paymentCodes.GOOGLE_PAY]: onRefundGooglePay,
      [paymentCodes.CARD]: onRefundCard
    };

    const refundHandler = [paymentProviders.ADYEN, paymentProviders.THALYS].includes(provider) ? onRefundCard : refundMethod[method];

    return refundHandler({
      amount: refundAmount,
      pspReference,
      currency,
      bookingReference,
      paymentLocation,
      additionalDetails,
      hasHotel: isPackage || hasHotel(booking),
      compensationRecordsToUpdate
    });
  };

  const loading =
    isCardPrrRefund ||
    isVoucherPrrRefundLoading ||
    isBusinessAccountPrrRefund ||
    isProcessLoyaltyPrrRefund ||
    bookingItemsLoading ||
    getExtendedCompensationByTicketNumbersDataLoading;

  if (getExtendedCompensationByTicketNumbersDataLoading || bookingItemsLoading) {
    return <FullPageSpinner />;
  }

  const productCurrency =
    compensateLegs.flatMap(({ products }) => products).find((product) => !!product?.currency)?.currency ?? booking.currency;

  return (
    <>
      <PrrRefund
        itemRefs={itemRefs.split(',')}
        reference={reference}
        filteredProducts={filteredProducts}
        compensateLegs={compensateLegs}
        compensationItems={compensationItems}
        payments={refundablePayments}
        loading={loading}
        onSubmit={handleSubmit}
        passengers={passengers}
        customer={customer}
        additionalDetails={booking?.additionalDetails}
        currency={productCurrency}
        legs={[...booking.outbound, ...booking.inbound]}
        username={user.username}
      />
      <Modal
        isOpen={updateCompensationsInfo}
        title={<FormattedMessage id={updateCompensationsInfo?.modalTitle} />}
        subtitle={
          <>
            <FormattedMessage
              id="prrRefund.updatedRecords"
              values={{ ticketNumbers: updateCompensationsInfo?.updatedRecords?.join(', ') }}
            />
            <br />
            <FormattedMessage
              id="prrRefund.manuallyUpdateRecords"
              values={{ ticketNumbers: updateCompensationsInfo?.manuallyUpdateRecords?.join(', ') }}
            />
          </>
        }
        onClose={next}
      />
    </>
  );
};

Index.propTypes = {
  booking: bookingShape,
  location: locationShape,
  user: userShape,
  history: historyShape
};

export default withUser(withBooking(Index, BOOKING_QUERY));
